import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  styled,
  useMediaQuery,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  Pagination,
  IconButton,
  Chip,
  Button,
  PaginationItem,
  Tooltip,
} from "@mui/material";
import {
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";

import { AppDispatch, RootState } from "../../stores";
import {
  getAllPlans,
  deletePlanById,
  Plan,
} from "../../features/plan-management-module/planAction";
import { clearPlanMessages } from "../../features/plan-management-module/planSlice";
import DeleteDialog from "./DeleteDialog";

const Container = styled(Box)(({ theme }) => ({
  minHeight: "90vh",
  background: "#fff",
  display: "flex",
  flexDirection: "row",
  overflow: "hidden",
  transform: "scale(0.95)",
  transformOrigin: "center",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
  [theme.breakpoints.down("sm")]: {
    minHeight: "85vh",
  },
}));

const StyledTable = styled(Table)(({ theme }) => ({
  minWidth: 650,
  width: "100%",
  tableLayout: "auto",
  [theme.breakpoints.down("md")]: {
    minWidth: 600,
  },
  [theme.breakpoints.down("sm")]: {
    minWidth: 500,
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: "0.8rem",
  color: "#4b5b5d",
  fontWeight: 500,
  padding: theme.spacing(0.3),
  whiteSpace: "nowrap",
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.75rem",
    padding: theme.spacing(1),
  },
}));

const StyledTableHeadCell = styled(StyledTableCell)(({ theme }) => ({
  backgroundColor: "#f5f0e7",
  fontWeight: 600,
  color: "#4b5b5d",
  padding: theme.spacing(1.2),
  lineHeight: "1rem",
  whiteSpace: "nowrap",
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.85rem",
    padding: theme.spacing(2),
    lineHeight: "0.5rem",
  },
}));

const FooterContainer = styled(Box)(({ theme }) => ({
  position: "fixed",
  bottom: 0,
  left: 0,
  right: 0,
  background: "#fff",
  padding: theme.spacing(2),
  zIndex: 1000,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  gap: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    padding: theme.spacing(1),
  },
}));

const ModePill = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  borderRadius: 12,
  padding: "4px 12px",
  fontSize: "0.75rem",
  fontWeight: 600,
  textTransform: "capitalize",
});

const EstimatorChip = styled(Chip)(({ theme }) => ({
  backgroundColor: "#e3f2fd",
  color: "#1976d2",
  fontWeight: 500,
  fontSize: "0.65rem",
  height: 20,
  marginRight: theme.spacing(0.5),
  marginBottom: theme.spacing(0.5),
  "& .MuiChip-label": {
    padding: theme.spacing(0, 0.5),
  },
}));

const EstimatorLimitText = styled(Typography)(({ theme }) => ({
  fontSize: "0.7rem",
  color: "#666",
  marginTop: theme.spacing(0.5),
}));

// Add the LoaderContainer and LoaderText styled components
const LoaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  width: "100%",
  minHeight: "300px",
}));

const LoaderText = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
  fontSize: "0.9rem",
  color: "#4b5b5d",
  fontWeight: 500,
}));

interface TablePlan {
  no: number;
  planName: string;
  price: string;
  duration: string;
  fleetEstimatorReportLimit: number;
  siteEstimatorReportLimit: number;
  isFleetEstimator: boolean;
  isSiteEstimator: boolean;
  mode: "public" | "private";
  assignName: string;
  id: string;
  isPopular?: boolean;
}

const Subscription: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const {
    plans,
    loading: plansLoading,
    error: plansError,
    pagination,
  } = useSelector((state: RootState) => state.planManagement);

  const [tablePlans, setTablePlans] = useState<TablePlan[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);

  useEffect(() => {
    dispatch(getAllPlans({ page: currentPage, limit: pageSize }));
    return () => {
      dispatch(clearPlanMessages());
    };
  }, [dispatch, currentPage, pageSize]);

  useEffect(() => {
    const mappedPlans: TablePlan[] = plans.map((plan: Plan, index: number) => ({
      no: (currentPage - 1) * pageSize + index + 1,
      planName: plan.planName,
      price: `$${plan.price}`,
      duration: plan.duration,
      fleetEstimatorReportLimit: plan.fleetEstimatorReportLimit || 0,
      siteEstimatorReportLimit: plan.siteEstimatorReportLimit || 0,
      isFleetEstimator: plan.isFleetEstimator || false,
      isSiteEstimator: plan.isSiteEstimator || false,
      mode: plan.mode,
      assignName: plan.assignName || "N/A",
      id: plan.id,
      isPopular: plan.isPopular,
    }));
    setTablePlans(mappedPlans);
  }, [plans, currentPage, pageSize]);

  const totalPages = pagination?.totalPages || 1;

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };

  const handlePageSizeChange = (event: any) => {
    setPageSize(event.target.value);
    setCurrentPage(1);
  };

  const handleView = (planId: string) => {
    navigate(`/main/subscriptions/view-plan/${planId}`);
  };

  const handleEdit = (planId: string) => {
    navigate(`/main/subscriptions/edit-plan/${planId}`);
  };

  const handleDelete = (planId: string) => {
    const plan = plans.find((p) => p.id === planId);
    if (plan) {
      setSelectedPlan(plan);
      setDeleteDialogOpen(true);
    }
  };

  const handleDeleteConfirm = () => {
    if (selectedPlan) {
      dispatch(deletePlanById(selectedPlan.id)).then(() => {
        setDeleteDialogOpen(false);
        setSelectedPlan(null);
        dispatch(getAllPlans({ page: currentPage, limit: pageSize }));
      });
    }
  };

  const getModeColor = (mode: "public" | "private") => {
    return mode === "public"
      ? { backgroundColor: "#E7F5FF", color: "#0B7285" }
      : { backgroundColor: "#F3F0FF", color: "#5F3DC4" };
  };

  const formatDuration = (duration: string) => {
    switch (duration) {
      case "monthly":
        return "Monthly";
      case "yearly":
        return "Yearly";
      case "custom":
        return "Custom";
      default:
        return duration;
    }
  };

  // Helper function to render estimator chips
  const renderEstimatorChips = (plan: TablePlan) => {
    const chips = [];

    if (plan.isFleetEstimator) {
      chips.push(<EstimatorChip key="fleet" label="Fleet" size="small" />);
    }

    if (plan.isSiteEstimator) {
      chips.push(<EstimatorChip key="site" label="Site" size="small" />);
    }

    return chips;
  };

  // Helper function to render estimator limits
  const renderEstimatorLimits = (plan: TablePlan) => {
    const limits = [];

    if (plan.isFleetEstimator && plan.fleetEstimatorReportLimit > 0) {
      limits.push(
        <EstimatorLimitText key="fleet">
          Fleet: {plan.fleetEstimatorReportLimit} reports
        </EstimatorLimitText>
      );
    }

    if (plan.isSiteEstimator && plan.siteEstimatorReportLimit > 0) {
      limits.push(
        <EstimatorLimitText key="site">
          Site: {plan.siteEstimatorReportLimit} reports
        </EstimatorLimitText>
      );
    }

    return limits;
  };

  const renderPaginationItems = (item: any) => {
    const { page: itemPage, type } = item;
    if (type === "previous" || type === "next") {
      return <PaginationItem {...item} />;
    }
    if (itemPage === currentPage || itemPage === currentPage + 1) {
      return <PaginationItem {...item} />;
    }
    return null;
  };

  return (
    <Container>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        style={{
          width: "100%",
          maxWidth: "auto",
          margin: "0 auto",
          paddingBottom: "80px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography variant="h5" fontWeight={600} color="#4b5b5d">
            Subscription Plans
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
              justifyContent: isMobile ? "space-between" : "flex-end",
              width: isMobile ? "100%" : "auto",
            }}
          >
            <Button
              variant="contained"
              onClick={() => navigate("/main/subscriptions/create-plan")}
              sx={{
                fontSize: isMobile ? "0.75rem" : "0.875rem",
                padding: isMobile ? "6px 12px" : "8px 16px",
                width: isMobile ? "100%" : "auto",
                maxWidth: isMobile ? "120px" : "none",
                background: "linear-gradient(to top, #758d8e, #4b5b5d)",
                color: "#fff",
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 2,
                "&:hover": {
                  backgroundColor: "#354142",
                },
              }}
            >
              Add Plan
            </Button>
          </Box>
        </Box>

        {/* Enhanced Loading State */}
        {plansLoading && (
          <LoaderContainer>
            <CircularProgress
              size={40}
              thickness={4}
              sx={{
                color: "#4b5b5d",
              }}
            />
            <LoaderText>Loading subscription plans...</LoaderText>
          </LoaderContainer>
        )}

        {/* Error State */}
        {!plansLoading && plansError && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              my: 2,
              minHeight: "200px",
            }}
          >
            <Typography
              color="error"
              sx={{
                textAlign: "center",
                fontSize: "0.9rem",
                fontWeight: 500,
              }}
            >
              {plansError}
            </Typography>
          </Box>
        )}

        {/* Success State */}
        {!plansLoading && !plansError && (
          <Box
            sx={{
              overflowX: "auto",
              "-webkit-overflow-scrolling": "touch",
              scrollbarWidth: "thin",
              "&::-webkit-scrollbar": {
                height: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#4b5b5d",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "#354142",
              },
            }}
          >
            <StyledTable size={isMobile ? "small" : "medium"}>
              <TableHead sx={{ background: "#f5f0e7" }}>
                <TableRow>
                  <StyledTableHeadCell>No.</StyledTableHeadCell>
                  <StyledTableHeadCell>Plan Name</StyledTableHeadCell>
                  <StyledTableHeadCell>Price</StyledTableHeadCell>
                  <StyledTableHeadCell>Duration</StyledTableHeadCell>
                  <StyledTableHeadCell>Estimators</StyledTableHeadCell>
                  <StyledTableHeadCell>Report Limits</StyledTableHeadCell>
                  <StyledTableHeadCell>Plan Type</StyledTableHeadCell>
                  <StyledTableHeadCell>Assign Name</StyledTableHeadCell>
                  <StyledTableHeadCell>Actions</StyledTableHeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tablePlans.length === 0 && !plansError ? (
                  <TableRow>
                    <StyledTableCell colSpan={9} align="center" sx={{ py: 3 }}>
                      <Typography variant="h6" color="#4b5b5d" fontWeight={500}>
                        No Plans Available
                      </Typography>
                    </StyledTableCell>
                  </TableRow>
                ) : (
                  tablePlans.map((plan) => (
                    <TableRow
                      key={plan.id}
                      sx={{
                        "&:nth-of-type(odd)": { backgroundColor: "#f8f8f8" },
                        "&:hover": { backgroundColor: "#e0e0e0" },
                      }}
                    >
                      <StyledTableCell>{plan.no}.</StyledTableCell>
                      <StyledTableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          {plan.planName}
                          {plan.isPopular && (
                            <Chip
                              label="Popular"
                              size="small"
                              color="success"
                            />
                          )}
                        </Box>
                      </StyledTableCell>
                      <StyledTableCell>{plan.price}</StyledTableCell>
                      <StyledTableCell>
                        {formatDuration(plan.duration)}
                      </StyledTableCell>
                      <StyledTableCell>
                        <Box sx={{ display: "flex", flexWrap: "wrap" }}>
                          {renderEstimatorChips(plan)}
                        </Box>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Box>{renderEstimatorLimits(plan)}</Box>
                      </StyledTableCell>
                      <StyledTableCell>
                        <ModePill sx={getModeColor(plan.mode)}>
                          {plan.mode}
                        </ModePill>
                      </StyledTableCell>
                      <StyledTableCell>{plan.assignName}</StyledTableCell>
                      <StyledTableCell>
                        <Box
                          sx={{
                            display: "flex",
                            gap: isMobile ? 1 : 0,
                            flexDirection: "row",
                            alignItems: "center",
                          }}
                        >
                          <Tooltip title="View Plan">
                            <IconButton
                              size={isMobile ? "small" : "medium"}
                              onClick={() => handleView(plan.id)}
                              sx={{ color: "#4b5b5d" }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Plan">
                            <IconButton
                              size={isMobile ? "small" : "medium"}
                              onClick={() => handleEdit(plan.id)}
                              sx={{ color: "#4b5b5d" }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Plan">
                            <IconButton
                              size={isMobile ? "small" : "medium"}
                              onClick={() => handleDelete(plan.id)}
                              sx={{ color: "#4b5b5d" }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </StyledTableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </StyledTable>
          </Box>
        )}

        <DeleteDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          selectedPlan={selectedPlan}
          handleDeleteConfirm={handleDeleteConfirm}
        />

        {!plansLoading && (
          <FooterContainer>
            <Typography
              variant="body2"
              color="#4b5b5d"
              sx={{
                display: "flex",
                justifyContent: isMobile ? "center" : "flex-end",
                width: isMobile ? "100%" : "auto",
              }}
            >
              Showing {(currentPage - 1) * pageSize + 1} to{" "}
              {Math.min(currentPage * pageSize, pagination?.totalPlans || 0)} of{" "}
              {pagination?.totalPlans || 0} entries
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Typography variant="body2" color="#4b5b5d">
                Show:
              </Typography>
              <FormControl size="small" sx={{ minWidth: 80 }}>
                <Select
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  sx={{
                    "& .MuiSelect-select": {
                      padding: "4px 8px",
                      fontSize: "0.8rem",
                    },
                  }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="body2" color="#4b5b5d">
                entries
              </Typography>
            </Box>

            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size={isMobile ? "small" : "medium"}
              renderItem={renderPaginationItems}
              sx={{
                display: "flex",
                justifyContent: isMobile ? "center" : "flex-end",
                width: isMobile ? "100%" : "auto",
                "& .MuiPaginationItem-root": {
                  color: "#4b5b5d",
                  "&.Mui-selected": {
                    backgroundColor: "#758d88",
                    color: "#f8f9fa",
                    fontWeight: 600,
                    "&:hover": {
                      backgroundColor: "#4b5b5d",
                    },
                  },
                  "&:hover": {
                    backgroundColor: "rgba(75, 91, 93, 0.04)",
                  },
                },
              }}
            />
          </FooterContainer>
        )}
      </motion.div>
    </Container>
  );
};

export default Subscription;
